import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const BiasResultsDisplay = ({ results, onCopyResults, onNewAnalysis }) => {
  const [copiedSection, setCopiedSection] = useState(null);

  if (!results) return null;

  const getBiasLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'low':
        return 'text-success bg-success/10 border-success/20';
      case 'medium':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'high':
        return 'text-error bg-error/10 border-error/20';
      default:
        return 'text-muted-foreground bg-muted/10 border-border';
    }
  };

  const getBiasIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'political':
        return 'Vote';
      case 'cultural':
        return 'Globe';
      case 'linguistic':
        return 'MessageSquare';
      case 'gender':
        return 'Users';
      case 'racial':
        return 'Heart';
      default:
        return 'AlertTriangle';
    }
  };

  const handleCopySection = async (content, sectionName) => {
    try {
      await navigator.clipboard?.writeText(content);
      setCopiedSection(sectionName);
      setTimeout(() => setCopiedSection(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const formatResultsForCopy = () => {
    return `BIAS DETECTION ANALYSIS RESULTS
Generated on: ${new Date()?.toLocaleDateString()}

OVERALL BIAS LEVEL: ${results?.overallBias?.level || 'Unknown'} (${results?.overallBias?.score || 0}/100)

DETECTED BIAS TYPES:
${results?.biasTypes?.map(bias => `• ${bias?.type}: ${bias?.severity} - ${bias?.description}`)?.join('\n') || 'None detected'}

KEY FINDINGS:
${results?.keyFindings?.map(finding => `• ${finding}`)?.join('\n') || 'No specific findings'}

IMPROVEMENT RECOMMENDATIONS:
${results?.recommendations?.map(rec => `• ${rec}`)?.join('\n') || 'No recommendations available'}

EVIDENCE EXAMPLES:
${results?.evidence?.map(ev => `• "${ev?.text}" - ${ev?.explanation}`)?.join('\n') || 'No evidence provided'}`;
  };

  return (
    <div className="mt-8 space-y-6 animate-in slide-in-from-bottom-4 duration-500">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-heading font-semibold text-foreground">
          Analysis Results
        </h3>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onCopyResults(formatResultsForCopy())}
            iconName="Copy"
            iconPosition="left"
            iconSize={16}
          >
            Copy All
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onNewAnalysis}
            iconName="RotateCcw"
            iconPosition="left"
            iconSize={16}
          >
            New Analysis
          </Button>
        </div>
      </div>
      {/* Overall Bias Score */}
      <div className="bg-card rounded-xl border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-medium text-foreground">Overall Bias Assessment</h4>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleCopySection(`Overall Bias: ${results?.overallBias?.level} (${results?.overallBias?.score}/100)`, 'overall')}
            iconName={copiedSection === 'overall' ? 'Check' : 'Copy'}
            iconSize={16}
            className="opacity-60 hover:opacity-100"
          />
        </div>
        
        <div className="flex items-center space-x-4">
          <div className={`px-4 py-2 rounded-lg border font-medium ${getBiasLevelColor(results?.overallBias?.level)}`}>
            {results?.overallBias?.level || 'Unknown'} Bias
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between text-sm text-muted-foreground mb-1">
              <span>Bias Score</span>
              <span>{results?.overallBias?.score || 0}/100</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-500 ${
                  (results?.overallBias?.score || 0) <= 30 ? 'bg-success' :
                  (results?.overallBias?.score || 0) <= 60 ? 'bg-warning' : 'bg-error'
                }`}
                style={{ width: `${Math.min(results?.overallBias?.score || 0, 100)}%` }}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Detected Bias Types */}
      {results?.biasTypes && results?.biasTypes?.length > 0 && (
        <div className="bg-card rounded-xl border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-foreground">Detected Bias Types</h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleCopySection(
                results?.biasTypes?.map(bias => `${bias?.type}: ${bias?.severity} - ${bias?.description}`)?.join('\n'),
                'types'
              )}
              iconName={copiedSection === 'types' ? 'Check' : 'Copy'}
              iconSize={16}
              className="opacity-60 hover:opacity-100"
            />
          </div>
          
          <div className="space-y-3">
            {results?.biasTypes?.map((bias, index) => (
              <div key={index} className="flex items-start space-x-3 p-3 bg-muted/30 rounded-lg">
                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name={getBiasIcon(bias?.type)} size={16} color="var(--color-primary)" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h5 className="font-medium text-foreground capitalize">{bias?.type} Bias</h5>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getBiasLevelColor(bias?.severity)}`}>
                      {bias?.severity}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">{bias?.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Key Findings */}
      {results?.keyFindings && results?.keyFindings?.length > 0 && (
        <div className="bg-card rounded-xl border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-foreground">Key Findings</h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleCopySection(results?.keyFindings?.join('\n'), 'findings')}
              iconName={copiedSection === 'findings' ? 'Check' : 'Copy'}
              iconSize={16}
              className="opacity-60 hover:opacity-100"
            />
          </div>
          
          <ul className="space-y-2">
            {results?.keyFindings?.map((finding, index) => (
              <li key={index} className="flex items-start space-x-3">
                <Icon name="ArrowRight" size={16} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
                <span className="text-sm text-muted-foreground">{finding}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* Evidence Examples */}
      {results?.evidence && results?.evidence?.length > 0 && (
        <div className="bg-card rounded-xl border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-foreground">Evidence Examples</h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleCopySection(
                results?.evidence?.map(ev => `"${ev?.text}" - ${ev?.explanation}`)?.join('\n'),
                'evidence'
              )}
              iconName={copiedSection === 'evidence' ? 'Check' : 'Copy'}
              iconSize={16}
              className="opacity-60 hover:opacity-100"
            />
          </div>
          
          <div className="space-y-4">
            {results?.evidence?.map((item, index) => (
              <div key={index} className="border-l-4 border-primary/30 pl-4 py-2">
                <blockquote className="text-sm text-foreground italic mb-2">
                  "{item?.text}"
                </blockquote>
                <p className="text-xs text-muted-foreground">{item?.explanation}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Recommendations */}
      {results?.recommendations && results?.recommendations?.length > 0 && (
        <div className="bg-card rounded-xl border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-foreground">Improvement Recommendations</h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleCopySection(results?.recommendations?.join('\n'), 'recommendations')}
              iconName={copiedSection === 'recommendations' ? 'Check' : 'Copy'}
              iconSize={16}
              className="opacity-60 hover:opacity-100"
            />
          </div>
          
          <div className="space-y-3">
            {results?.recommendations?.map((recommendation, index) => (
              <div key={index} className="flex items-start space-x-3 p-3 bg-success/5 border border-success/20 rounded-lg">
                <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                <span className="text-sm text-foreground">{recommendation}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Analysis Metadata */}
      <div className="bg-muted/30 rounded-lg p-4">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Analysis completed on {new Date()?.toLocaleDateString()} at {new Date()?.toLocaleTimeString()}</span>
          <span>Powered by AI Bias Detection</span>
        </div>
      </div>
    </div>
  );
};

export default BiasResultsDisplay;